/**
 * Response Validator
 * 
 * Validates AI responses to catch hallucinations, format issues, and quality problems.
 * Ensures surgical edits maintain structural integrity and don't introduce unwanted content.
 * 
 * @module responseValidator
 */

/**
 * Validation severity levels
 * @enum {string}
 */
export const ValidationSeverity = {
    ERROR: 'error',     // Critical issue - response should be rejected
    WARNING: 'warning', // Potential issue - should be flagged but usable
    INFO: 'info'        // Informational - no action needed
};

/**
 * Validation result structure
 * @typedef {object} ValidationResult
 * @property {boolean} isValid - Whether the response passes validation
 * @property {ValidationIssue[]} issues - List of issues found
 * @property {ValidationIssue[]} warnings - Non-critical issues
 * @property {object} stats - Statistics about the response
 */

/**
 * Individual validation issue
 * @typedef {object} ValidationIssue
 * @property {string} code - Issue code identifier
 * @property {string} message - Human-readable message
 * @property {ValidationSeverity} severity - Issue severity
 * @property {string} [details] - Additional details
 */

/**
 * Patterns that indicate conversational filler
 * @constant {RegExp[]}
 */
const CONVERSATIONAL_FILLER_PATTERNS = [
    /^(here|this is|I have|I've|sure|certainly|absolutely|of course)/i,
    /edited (version|text|content|post)/i,
    /hope (this|it) helps/i,
    /let me know/i,
    /I (can|will|would) (help|assist|modify)/i,
    /is there anything else/i,
    /feel free to/i,
    /as you (can see|requested)/i,
    /I've (made|applied|updated)/i
];

/**
 * Patterns that indicate AI commentary or meta-discussion
 * @constant {RegExp[]}
 */
const META_COMMENTARY_PATTERNS = [
    /I (changed|modified|updated|added|removed)/i,
    /the (main|primary|key) (change|difference|modification)/i,
    /I (kept|preserved|maintained)/i,
    /as per your (request|instruction)/i,
    /following your (guidelines|instructions)/i
];

/**
 * Patterns that indicate potential hallucination
 * @constant {RegExp[]}
 */
const HALLUCINATION_INDICATORS = [
    /according to (my|the) (knowledge|training|data)/i,
    /I (don't|cannot|can't) (verify|confirm)/i,
    /this may not be (accurate|correct)/i,
    /please (verify|check|confirm)/i
];

/**
 * Validate an edit response against the source text
 * 
 * @param {string} response - The AI response (edited text)
 * @param {string} sourceText - The original source text
 * @param {object} options - Validation options
 * @param {boolean} options.strict - Enable strict validation
 * @param {number} options.maxLengthChange - Maximum allowed length change ratio
 * @returns {ValidationResult} Validation result
 */
export const validateEditResponse = (response, sourceText, options = {}) => {
    const {
        strict = false,
        maxLengthChange = 3.0 // Maximum 3x length change
    } = options;

    const issues = [];
    const warnings = [];
    const stats = calculateResponseStats(response, sourceText);

    // Check for conversational filler
    const fillerIssues = checkConversationalFiller(response);
    issues.push(...fillerIssues);

    // Check for meta commentary
    const metaIssues = checkMetaCommentary(response);
    issues.push(...metaIssues);

    // Check for hallucination indicators
    const hallucinationIssues = checkHallucinationIndicators(response);
    issues.push(...hallucinationIssues);

    // Check structural preservation
    const structuralIssues = checkStructuralPreservation(response, sourceText, strict);
    warnings.push(...structuralIssues);

    // Check length reasonableness
    const lengthIssues = checkLengthChange(response, sourceText, maxLengthChange);
    warnings.push(...lengthIssues);

    // Check content preservation (key elements)
    const preservationIssues = checkContentPreservation(response, sourceText);
    warnings.push(...preservationIssues);

    // Separate errors from warnings
    const errors = issues.filter(i => i.severity === ValidationSeverity.ERROR);
    const warningList = issues.filter(i => i.severity === ValidationSeverity.WARNING);
    warnings.push(...warningList);

    return {
        isValid: errors.length === 0,
        errors: errors.length > 0 ? errors : undefined,
        warnings: warnings.length > 0 ? warnings : undefined,
        stats
    };
};

/**
 * Calculate statistics comparing response to source
 * 
 * @param {string} response - The AI response
 * @param {string} sourceText - The original source text
 * @returns {object} Statistics object
 */
const calculateResponseStats = (response, sourceText) => {
    const responseChars = response?.length || 0;
    const sourceChars = sourceText?.length || 0;
    const responseWords = (response?.trim().split(/\s+/) || []).filter(Boolean).length;
    const sourceWords = (sourceText?.trim().split(/\s+/) || []).filter(Boolean).length;

    const responseParagraphs = (response?.split(/\n\n+/) || []).filter(Boolean).length;
    const sourceParagraphs = (sourceText?.split(/\n\n+/) || []).filter(Boolean).length;

    const responseHashtags = (response?.match(/#[\w]+/g) || []).length;
    const sourceHashtags = (sourceText?.match(/#[\w]+/g) || []).length;

    const responseMentions = (response?.match(/@[\w]+/g) || []).length;
    const sourceMentions = (sourceText?.match(/@[\w]+/g) || []).length;

    return {
        response: {
            characters: responseChars,
            words: responseWords,
            paragraphs: responseParagraphs,
            hashtags: responseHashtags,
            mentions: responseMentions
        },
        source: {
            characters: sourceChars,
            words: sourceWords,
            paragraphs: sourceParagraphs,
            hashtags: sourceHashtags,
            mentions: sourceMentions
        },
        diff: {
            characters: responseChars - sourceChars,
            words: responseWords - sourceWords,
            paragraphs: responseParagraphs - sourceParagraphs,
            hashtags: responseHashtags - sourceHashtags,
            mentions: responseMentions - sourceMentions
        },
        ratio: {
            characters: sourceChars > 0 ? responseChars / sourceChars : 0,
            words: sourceWords > 0 ? responseWords / sourceWords : 0
        }
    };
};

/**
 * Check for conversational filler in response
 * 
 * @param {string} response - The response to check
 * @returns {ValidationIssue[]} Issues found
 */
const checkConversationalFiller = (response) => {
    const issues = [];

    for (const pattern of CONVERSATIONAL_FILLER_PATTERNS) {
        if (pattern.test(response)) {
            issues.push({
                code: 'CONVERSATIONAL_FILLER',
                message: 'Response contains conversational filler',
                severity: ValidationSeverity.ERROR,
                details: `Matched pattern: ${pattern.toString()}`
            });
            break; // Only report once
        }
    }

    return issues;
};

/**
 * Check for meta commentary in response
 * 
 * @param {string} response - The response to check
 * @returns {ValidationIssue[]} Issues found
 */
const checkMetaCommentary = (response) => {
    const issues = [];

    for (const pattern of META_COMMENTARY_PATTERNS) {
        if (pattern.test(response)) {
            issues.push({
                code: 'META_COMMENTARY',
                message: 'Response contains AI meta-commentary',
                severity: ValidationSeverity.WARNING,
                details: `Matched pattern: ${pattern.toString()}`
            });
            break;
        }
    }

    return issues;
};

/**
 * Check for hallucination indicators
 * 
 * @param {string} response - The response to check
 * @returns {ValidationIssue[]} Issues found
 */
const checkHallucinationIndicators = (response) => {
    const issues = [];

    for (const pattern of HALLUCINATION_INDICATORS) {
        if (pattern.test(response)) {
            issues.push({
                code: 'HALLUCINATION_INDICATOR',
                message: 'Response contains potential hallucination indicators',
                severity: ValidationSeverity.WARNING,
                details: `Matched pattern: ${pattern.toString()}`
            });
            break;
        }
    }

    return issues;
};

/**
 * Check structural preservation between source and response
 * 
 * @param {string} response - The edited response
 * @param {string} sourceText - The original source
 * @param {boolean} strict - Whether to use strict validation
 * @returns {ValidationIssue[]} Issues found
 */
const checkStructuralPreservation = (response, sourceText, strict) => {
    const issues = [];
    const stats = calculateResponseStats(response, sourceText);

    // Check paragraph count change
    const paragraphChange = Math.abs(stats.diff.paragraphs);
    if (paragraphChange > 2) {
        issues.push({
            code: 'STRUCTURAL_CHANGE',
            message: `Significant structural change: ${paragraphChange} paragraph difference`,
            severity: strict ? ValidationSeverity.ERROR : ValidationSeverity.WARNING,
            details: `Source: ${stats.source.paragraphs} paragraphs, Response: ${stats.response.paragraphs} paragraphs`
        });
    }

    // Check for removed hashtags (unless explicitly intended)
    if (stats.diff.hashtags < 0) {
        issues.push({
            code: 'HASHTAG_REMOVAL',
            message: `${Math.abs(stats.diff.hashtags)} hashtag(s) were removed`,
            severity: ValidationSeverity.INFO,
            details: `Source: ${stats.source.hashtags}, Response: ${stats.response.hashtags}`
        });
    }

    return issues;
};

/**
 * Check if length change is reasonable
 * 
 * @param {string} response - The edited response
 * @param {string} sourceText - The original source
 * @param {number} maxRatio - Maximum allowed ratio
 * @returns {ValidationIssue[]} Issues found
 */
const checkLengthChange = (response, sourceText, maxRatio) => {
    const issues = [];
    const stats = calculateResponseStats(response, sourceText);

    if (stats.ratio.characters > maxRatio) {
        issues.push({
            code: 'EXCESSIVE_LENGTH_INCREASE',
            message: `Response is ${(stats.ratio.characters * 100).toFixed(0)}% of original (max: ${maxRatio * 100}%)`,
            severity: ValidationSeverity.WARNING,
            details: `Characters: ${stats.source.characters} -> ${stats.response.characters}`
        });
    } else if (stats.ratio.characters < 0.3) {
        issues.push({
            code: 'EXCESSIVE_LENGTH_DECREASE',
            message: `Response is only ${(stats.ratio.characters * 100).toFixed(0)}% of original`,
            severity: ValidationSeverity.WARNING,
            details: `Characters: ${stats.source.characters} -> ${stats.response.characters}`
        });
    }

    return issues;
};

/**
 * Check if key content elements are preserved
 * 
 * @param {string} response - The edited response
 * @param {string} sourceText - The original source
 * @returns {ValidationIssue[]} Issues found
 */
const checkContentPreservation = (response, sourceText) => {
    const issues = [];

    // Extract key elements from source
    const sourceHashtags = new Set((sourceText?.match(/#[\w]+/g) || []).map(h => h.toLowerCase()));
    const responseHashtags = new Set((response?.match(/#[\w]+/g) || []).map(h => h.toLowerCase()));

    // Check for preserved hashtags
    const preservedHashtags = [...sourceHashtags].filter(h => responseHashtags.has(h));
    const removedHashtags = [...sourceHashtags].filter(h => !responseHashtags.has(h));

    if (removedHashtags.length > 0 && preservedHashtags.length === 0) {
        issues.push({
            code: 'ALL_HASHTAGS_REMOVED',
            message: 'All original hashtags were removed',
            severity: ValidationSeverity.INFO,
            details: `Removed: ${removedHashtags.join(', ')}`
        });
    }

    // Check for mentions preservation
    const sourceMentions = new Set((sourceText?.match(/@[\w]+/g) || []));
    const responseMentions = new Set((response?.match(/@[\w]+/g) || []));
    const removedMentions = [...sourceMentions].filter(m => !responseMentions.has(m));

    if (removedMentions.length > 0) {
        issues.push({
            code: 'MENTIONS_REMOVED',
            message: `${removedMentions.length} mention(s) were removed`,
            severity: ValidationSeverity.INFO,
            details: `Removed: ${removedMentions.join(', ')}`
        });
    }

    return issues;
};

/**
 * Sanitize an edit response by removing JSON wrapper and cleaning content
 * 
 * @param {string} response - Raw response from AI
 * @returns {string} Sanitized content
 */
export const sanitizeEditResponse = (response) => {
    if (!response) return response;

    let cleaned = response;

    // Remove markdown code blocks if present
    cleaned = cleaned.replace(/^```json?\n?/i, '').replace(/\n?```$/i, '');

    // Trim whitespace
    cleaned = cleaned.trim();

    // Try to parse as JSON
    if (cleaned.startsWith('{') || cleaned.startsWith('[')) {
        try {
            const parsed = JSON.parse(cleaned);
            // Extract edited_text field if present
            if (parsed.edited_text) {
                return parsed.edited_text;
            }
            // If it's just a string in an object
            if (typeof parsed === 'object' && Object.keys(parsed).length === 1) {
                const value = Object.values(parsed)[0];
                if (typeof value === 'string') {
                    return value;
                }
            }
        } catch (e) {
            // Not valid JSON, return as-is
        }
    }

    return cleaned;
};

/**
 * Validate JSON response format
 * 
 * @param {string} response - Response to validate
 * @returns {object} Parsed and validated JSON or error
 */
export const validateJsonResponse = (response) => {
    try {
        const parsed = JSON.parse(response);

        if (typeof parsed !== 'object' || parsed === null) {
            return {
                isValid: false,
                error: 'Response is not a valid object',
                parsed: null
            };
        }

        if (!parsed.edited_text && !parsed.content) {
            return {
                isValid: false,
                error: 'Response missing required field: edited_text or content',
                parsed
            };
        }

        return {
            isValid: true,
            parsed,
            content: parsed.edited_text || parsed.content
        };
    } catch (e) {
        return {
            isValid: false,
            error: `JSON parse error: ${e.message}`,
            parsed: null
        };
    }
};

/**
 * Create a validation report for logging/debugging
 * 
 * @param {ValidationResult} result - Validation result
 * @param {string} response - The response that was validated
 * @param {string} sourceText - The source text
 * @returns {string} Formatted report
 */
export const createValidationReport = (result, response, sourceText) => {
    const lines = [
        '=== Response Validation Report ===',
        `Valid: ${result.isValid ? 'YES' : 'NO'}`,
        ''
    ];

    if (result.errors?.length > 0) {
        lines.push('ERRORS:');
        result.errors.forEach(e => {
            lines.push(`  [${e.code}] ${e.message}`);
            if (e.details) lines.push(`    ${e.details}`);
        });
        lines.push('');
    }

    if (result.warnings?.length > 0) {
        lines.push('WARNINGS:');
        result.warnings.forEach(w => {
            lines.push(`  [${w.code}] ${w.message}`);
            if (w.details) lines.push(`    ${w.details}`);
        });
        lines.push('');
    }

    if (result.stats) {
        lines.push('STATISTICS:');
        lines.push(`  Source: ${result.stats.source.characters} chars, ${result.stats.source.words} words`);
        lines.push(`  Response: ${result.stats.response.characters} chars, ${result.stats.response.words} words`);
        lines.push(`  Ratio: ${(result.stats.ratio.characters * 100).toFixed(1)}%`);
    }

    return lines.join('\n');
};

export default {
    ValidationSeverity,
    validateEditResponse,
    sanitizeEditResponse,
    validateJsonResponse,
    createValidationReport,
    calculateResponseStats
};